import styles from './styles.module.scss';
import { useEffect, useState } from 'react';
import TabContentWrapper from 'pages/Profile/components/TabContentWrapper';
import classNames from 'classnames';
import { SearchFieldWithDebounce, Spinner } from 'components';
import { Select, Option } from 'components-antd';
import { Icons } from 'pages/Profile/components/icons';
import { transactionStatuses, transactionStatusesOptions } from 'settings/constants/transaction';
import { showErrorMessage } from 'helpers';
import { getTeamMemberTransactions } from 'api/teamList';
import TransactionsTable from './TransactionsTable';
import {
  inferRoleCategory,
  inferStatus,
} from 'pages/Workshop/Transactions/components/TransactionsAndProjectsTable/tableColumns';
import { getTransactionEndDate } from 'utils';
import moment from 'moment';

export type PreparedTransactionType = {
  Id: number;
  Address: string;
  Location: string;
  Type: string;
  SubType: string;
  Status: string;
  ClosingExpiryDate: string;
  SearchableString: string;
  Role: string;
  IsClosedOrCancelled: boolean;
  OverdueTasksCount: number;
  OverdueMilestonesCount: number;
  NewOffersCount: number;
  OfferLink: {
    EnableLink: boolean;
  };
  Showing: {
    EnableShowingLink: boolean;
  };
};

const Transactions = ({ contactId }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [transactionsList, setTransactionsList] = useState<PreparedTransactionType[]>([]);
  const [displayTransactions, setDisplayTransactions] = useState<PreparedTransactionType[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [trxType, setTrxType] = useState<any>('All');

  if (!contactId) return null;

  useEffect(() => {
    fetchMemberTransactions();
  }, []);

  useEffect(() => {
    if (transactionsList.length) {
      if (trxType === 'All') {
        const orderedTransactions = setTransactionsOrdering(transactionsList);
        setDisplayTransactions(orderedTransactions);
        return;
      }
      const filteredResults = transactionsList.filter((trx) => trx.Status === trxType);
      const orderedTransactions = setTransactionsOrdering(filteredResults);
      setDisplayTransactions(orderedTransactions);
    }
  }, [trxType, transactionsList]);

  const setTransactionsOrdering = (unorderedTransactions: any[]) => {
    const transactionsWithDate = unorderedTransactions.filter((trx) => !!trx.ClosingExpiryDate);
    const transactionsWithoutDate = unorderedTransactions.filter((trx) => !trx.ClosingExpiryDate);

    return [
      ...transactionsWithDate.sort((a, b) => {
        const dateA = moment(a.ClosingExpiryDate, 'MM/DD/YYYY').valueOf();
        const dateB = moment(b.ClosingExpiryDate, 'MM/DD/YYYY').valueOf();
        return dateA - dateB;
      }),
      ...transactionsWithoutDate,
    ];
  };

  const fetchMemberTransactions = async () => {
    setLoading(true);
    try {
      const response = await getTeamMemberTransactions({}, { agentId: contactId });
      if (response.status === 200 && response.data) {
        const { ClearToClose, Closed, Listed, UnderContract, Upcoming } = response.data;
        const allTransactions = [ClearToClose, Closed, Listed, UnderContract, Upcoming]
          .filter(Boolean)
          .flat();
        const preparedTransactions: PreparedTransactionType[] = allTransactions.map((trx) => {
          const propertyAddress = getAddress(trx.Property?.Address);
          const propertyLocation = getLocation(trx.Property?.Address);
          const trxType = inferRoleCategory(trx);
          const trxSubType = inferStatus(trx);
          const trxRole =
            trx?.ParticipatingAgent?.length === 1
              ? trx.ParticipatingAgent[0].Role?.Title
              : undefined;
          return {
            Id: trx.Id,
            Address: propertyAddress,
            Location: propertyLocation,
            Type: trxType || '-',
            SubType: trxSubType || '',
            Status: trx.Status,
            ClosingExpiryDate: getTransactionEndDate(trx.Status, trx, false, 'MM/DD/YYYY'),
            Role: trxRole ?? '-',
            OverdueTasksCount: trx.Tasks?.length
              ? trx.Tasks.filter((task) => task.StatusId === 6).length
              : 0,
            OverdueMilestonesCount: trx.Milestones?.length
              ? trx.Milestones.filter((stone) => stone.StatusId === 1).length
              : 0,
            NewOffersCount:
              trx.NewOffersCount && Number(trx.NewOffersCount) ? Number(trx.NewOffersCount) : 0,
            OfferLink: {
              EnableLink: !!trx.OfferLink?.EnableLink,
            },
            Showing: {
              EnableShowingLink: !!trx.Showing?.EnableShowingLink,
            },
            IsClosedOrCancelled:
              trx.Status === transactionStatuses.Closed ||
              trx.Status === transactionStatuses.Canceled,
            SearchableString: `${propertyAddress}${propertyLocation}${trxType}${trxSubType}${
              trx.Status
            }${trxRole ?? ''}`,
          };
        });
        setTransactionsList(preparedTransactions);
      }
    } catch (e) {
      showErrorMessage('Unable to fetch member transactions');
    }
    setLoading(false);
  };

  const getAddress = (address) => {
    if (!address) return '-';

    return `${address?.Line1}${address?.Line2 ? ` #${address?.Line2}` : ''}`;
  };

  const getLocation = (address) => {
    if (!address) return '';

    let location = address?.City ?? '';
    if (address?.State) location += `, ${address?.State}`;
    if (address?.Zip) location += `, ${address?.Zip}`;

    return location;
  };

  const onSelectChange = (value) => {
    setTrxType(value);
  };

  const getTypeSelector = () => (
    <Select
      size={'large'}
      defaultValue={'All'}
      popupClassName={styles.typeSelectorPopup}
      onChange={onSelectChange}
      placement={'bottomRight'}
      dropdownIconColor={'#262626'}
      suffixIcon={<Icons variant={Icons.DROPDOWN} />}
      listHeight={400}
    >
      <Option key={-1} value={'All'}>
        <span className={styles.label}>{'All'}</span>
      </Option>
      {transactionStatusesOptions.map((option, index) => (
        <Option key={index} value={option.value}>
          <span className={styles.label}>{option.name}</span>
        </Option>
      ))}
    </Select>
  );

  if (loading)
    return (
      <TabContentWrapper setMargin contentHeightFull>
        <div className={styles.transactionsTab}>
          <div className={classNames(styles.content, styles.contentLoading)}>
            <Spinner />
          </div>
        </div>
      </TabContentWrapper>
    );
  else if (!loading && !transactionsList.length)
    return (
      <TabContentWrapper setMargin contentHeightFull>
        <div className={styles.transactionsTab}>
          <div className={styles.content}>
            <div className={styles.empty}>No transactions to show.</div>
          </div>
        </div>
      </TabContentWrapper>
    );

  return (
    <TabContentWrapper setMargin contentHeight={'90%'}>
      <div className={styles.transactionsTab}>
        <div className={styles.header}>
          <div className={styles.left}>
            Transactions
            <span>{` (${displayTransactions.length})`}</span>
          </div>
          <div className={styles.right}>
            <SearchFieldWithDebounce
              className={styles.searchField}
              resetAction={() => {
                setSearchQuery('');
              }}
              doNotDispatch
              sendAction={(value: string) => setSearchQuery(value)}
              placeholder="Search"
              isExpandedVersion
            />
            {getTypeSelector()}
          </div>
        </div>
        <div className={styles.content}>
          <TransactionsTable transactions={displayTransactions} searchQuery={searchQuery} />
        </div>
      </div>
    </TabContentWrapper>
  );
};

export default Transactions;
